import React, { useState } from 'react';
import { useJazyk } from '../../context/LanguageContext';
import { toast } from 'sonner';
import { Lock, LockOpen, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useCrypto } from '../../context/CryptoContext';

/**
 * Pruh nad zprávami: zapnutí šifrování a odemčení klíče.
 *
 * Ukazuje se jen tehdy, když je co řešit. Když je odemčeno, je tu jen tichá
 * poznámka, že zprávy jsou šifrované – žádné velké gratulační okno.
 */
export default function EncryptionPanel() {
  const { t } = useJazyk();
  const { stav, zapni, odemkni } = useCrypto();
  const [heslo, setHeslo] = useState('');
  const [pracuje, setPracuje] = useState(false);

  const spust = async (co) => {
    if (!heslo) { toast.error(t('sif.zadejHeslo')); return; }
    setPracuje(true);
    try {
      const ok = co === 'zapnout' ? await zapni(heslo) : await odemkni(heslo);
      if (ok) {
        setHeslo('');
        toast.success(t(co === 'zapnout' ? 'sif.zapnuto' : 'sif.odemceno'));
      } else {
        toast.error(t('sif.hesloNesedi'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nepovedlo se to.');
    } finally {
      setPracuje(false);
    }
  };

  if (stav === 'nacita') return null;

  if (stav === 'nepodporovano') {
    return (
      <Ramecek barva="amber" ikona={AlertTriangle}>
        <p className="text-sm text-bloom-text">
          {t('sif.prohlizecNeumi')}
        </p>
      </Ramecek>
    );
  }

  if (stav === 'odemceno') {
    return (
      <div className="flex items-center gap-2 text-xs text-bloom-sub px-1 pb-2"
           data-testid="e2e-odemceno">
        <ShieldCheck className="w-3.5 h-3.5 text-bloom-mint" />
        {t('sif.noveJsouSifrovane')}
      </div>
    );
  }

  if (stav === 'zamceno') {
    return (
      <Ramecek barva="violet" ikona={Lock} testid="e2e-zamceno">
        <p className="font-heading font-bold text-bloom-text">{t('sif.odemkniZpravy')}</p>
        <p className="mt-1 text-sm text-bloom-sub">
          {t('sif.odemkniPopis')}
        </p>
        <Formular heslo={heslo} setHeslo={setHeslo} pracuje={pracuje}
                  popisek={t('sif.odemknout')} onOdeslat={() => spust('odemknout')}
                  testid="e2e-odemknout" />
      </Ramecek>
    );
  }

  return (
    <Ramecek barva="violet" ikona={LockOpen} testid="e2e-vypnuto">
      <p className="font-heading font-bold text-bloom-text">{t('sif.zapniSifrovani')}</p>
      <p className="mt-1 text-sm text-bloom-sub">
        {t('sif.zapniPopis')}
      </p>
      <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200/70 rounded-lg px-3 py-2">
        {t('sif.pozorA')} <strong>{t('sif.pozorTucne')}</strong>{t('sif.pozorB')}
      </p>
      <p className="mt-2 text-xs text-bloom-sub bg-bloom-bg border border-border/50 rounded-lg px-3 py-2 leading-relaxed">
        {t('sif.mobilA')} <strong>{t('sif.mobilTucne')}</strong>{t('sif.mobilB')}
      </p>
      <Formular heslo={heslo} setHeslo={setHeslo} pracuje={pracuje}
                popisek={t('sif.zapnoutSifrovani')} onOdeslat={() => spust('zapnout')}
                testid="e2e-zapnout" />
    </Ramecek>
  );
}

function Ramecek({ barva, ikona: Ikona, children, testid }) {
  const styly = {
    violet: 'bg-white border-bloom-violet/25',
    amber: 'bg-amber-50/70 border-amber-200/70',
  }[barva];
  return (
    <div className={`border rounded-2xl p-4 mb-3 ${styly}`} data-testid={testid}>
      <div className="flex gap-3">
        <Ikona className="w-5 h-5 text-bloom-violet shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}

function Formular({ heslo, setHeslo, pracuje, popisek, onOdeslat, testid }) {
  const { t } = useJazyk();
  return (
    <form className="mt-3 flex gap-2 flex-wrap"
          onSubmit={(e) => { e.preventDefault(); onOdeslat(); }}>
      <Input type="password" value={heslo} onChange={(e) => setHeslo(e.target.value)}
             placeholder={t('sif.tvojeHeslo')} autoComplete="current-password"
             className="flex-1 min-w-[180px]" data-testid={`${testid}-heslo`} />
      <Button type="submit" disabled={pracuje}
              className="bg-bloom-violet hover:bg-bloom-violet/90" data-testid={testid}>
        {pracuje ? 'Pracuji…' : popisek}
      </Button>
    </form>
  );
}
